import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Box } from "@mui/material";
import { FaStar } from "react-icons/fa";

import LegacyAutocompleteDropdown from "../../../../../../components/Custom/AutoCompleteDropdown";
import UnderlineButton from "../../../../../../components/Custom/UnderlineButton";
import { CustomInfoButton } from "../../../../../../components/Custom/CustomIconButton";
import { USER_ROLES } from "../../../../../../staticData/userRoles";
import styles from "../../sessions.module.css";
import type { PositionFormik, PositionOption } from "./positionForm.types";
import { FieldError } from "./PositionFormFields";

// Untyped (@ts-nocheck) legacy component, so every prop is optional.
const AutocompleteDropdown = LegacyAutocompleteDropdown as unknown as React.ComponentType<
  Record<string, unknown>
>;

type OptionProps = { option: PositionOption; onClick?: () => void };

const PINK = "rgb(209, 117, 182)";

const optionBoxStyle: React.CSSProperties = {
  position: "relative",
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  padding: "6px 8px",
  boxSizing: "border-box",
  border: `1px solid ${PINK}`,
  borderRadius: "4px",
  overflow: "visible",
  cursor: "pointer",
};

const rowStyle: React.CSSProperties = {
  width: "100%",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "0.5rem",
};

const briefStyle: React.CSSProperties = {
  margin: "0",
  opacity: "0.5",
  fontSize: "0.85rem",
  color: "rgb(255, 255, 255, 0.75)",
};

/** A catalog position in the dropdown: name, tags, info button and brief. */
const PositionOptionItem = ({ option, onClick }: OptionProps) => (
  <div style={optionBoxStyle} className="autocomplete-option" onClick={onClick}>
    <div style={rowStyle}>
      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
        <p style={{ color: "white", margin: "0" }}>{option.label}</p>
        {option?.tag?.length > 0 ? <FaStar color={PINK} /> : null}
        <span style={{ color: PINK }}>{option?.tag?.slice(0, 2)?.join(", ")}</span>
      </div>
      <div style={{ position: "relative" }}>
        <CustomInfoButton
          header={option.label}
          body={option?.positionDescription}
          equipments={option?.equipmentRequired}
        />
      </div>
    </div>
    <p style={briefStyle}>{option?.positionBrief}</p>
  </div>
);

/** Shown when the search has no match: shortcut to create a new position. */
const CreatePositionOption = ({ option, onClick }: OptionProps) => (
  <div style={optionBoxStyle} className="autocomplete-option">
    <div style={rowStyle}>
      <UnderlineButton title="Add Position" onClick={onClick} />
    </div>
    <p style={briefStyle}>{option?.positionBrief}</p>
  </div>
);

const toDropdownOption = (position: PositionOption): PositionOption => ({
  ...position,
  value: position?._id,
  label: position?.positionName,
});

const matchesSearch = (option: PositionOption, term: string) => {
  const needle = term?.toLowerCase?.() ?? "";
  return (
    option?.label?.toLowerCase?.()?.includes?.(needle) ||
    option?.tag?.some?.((tag) => tag?.toLowerCase?.()?.includes?.(needle))
  );
};

/** Multi-select search over the position catalog. */
type PositionSearchFieldProps = Pick<
  PositionFormik,
  "values" | "touched" | "errors" | "setFieldValue"
> & {
  searchTerm?: string;
  setSearchTerm?: (term: string) => void;
  options?: PositionOption[];
  filteredOptions?: PositionOption[];
  setFilteredOptions?: (options: PositionOption[]) => void;
};

const PositionSearchField = ({
  values,
  touched,
  errors,
  setFieldValue,
  searchTerm,
  setSearchTerm,
  options,
  filteredOptions,
  setFilteredOptions,
}: PositionSearchFieldProps) => {
  const navigate = useNavigate();
  const auth = useSelector(
    (store: { auth?: { userRole?: string; canPositionEdit?: boolean } }) => store.auth,
  );

  const canCreatePosition =
    auth?.userRole === USER_ROLES.SUPERADMIN ||
    auth?.userRole === USER_ROLES.ADMIN ||
    auth?.canPositionEdit;

  useEffect(() => {
    setFilteredOptions?.(options?.filter?.((option) => matchesSearch(option, searchTerm)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const handlePositionChange = (
    position: PositionOption | PositionOption[],
    _positionName: string,
    action: string,
  ) => {
    const selected = values?.positions || [];
    if (action === "onChange") {
      setFieldValue("positions", [...((position as PositionOption[]) || selected)]);
      return;
    }
    const single = position as PositionOption;
    if (action === "delete") {
      setFieldValue(
        "positions",
        selected.filter((item) => item?._id !== single?._id),
      );
    } else if (!selected.some((item) => item?._id === single?._id)) {
      setFieldValue("positions", [...selected, single]);
    }
  };

  const noMatch = Boolean(searchTerm) && filteredOptions?.length === 0;

  const dropdownOptions = !searchTerm
    ? options?.map?.(toDropdownOption)
    : !noMatch
      ? filteredOptions
      : canCreatePosition
        ? [{ value: "Add Position", label: "Add Position" }]
        : [];

  return (
    <Box className={styles.searchBox}>
      <Box className={`${styles.inputIconBox} ${styles.iconStart}`}>
        <AutocompleteDropdown
          Option={noMatch ? CreatePositionOption : PositionOptionItem}
          optionOnClick={
            noMatch
              ? () => navigate("/positions", { state: { openAddNewPositionModal: true } })
              : handlePositionChange
          }
          options={dropdownOptions}
          handleChangeOption={handlePositionChange}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          value={values?.positions?.map?.(toDropdownOption)}
        />
      </Box>
      <FieldError show={touched?.positions} message={errors?.positions} />
    </Box>
  );
};

export default PositionSearchField;
